import { ref, getDownloadURL } from 'firebase/storage';
import { useState, useEffect, useRef } from 'react';
import { storage } from "../firebaseConfig";


// ─── Singleton Service ────────────────────────────────────────────────────────

// const storage = getStorage(initializeApp(firebaseConfig));
const client = window.location.hostname.split('.')[0];

// In-memory cache persists for the lifetime of the app (SWR-style)
const logoCache = {};

// In-flight promise deduplication: prevents double-fetching the same logo
const inFlight = {};

/**
 * Fetches a logo URL from Firebase Storage.
 * Returns null if the logo does not exist.
 */
export async function getLogo(name) {
    if (logoCache[name] !== undefined) {
        return logoCache[name];
    }

    // Deduplicate concurrent requests for the same name
    if (inFlight[name]) {
        return inFlight[name];
    }

    const promise = (async () => {
        try {
            const refPath = `PrePaidCardLogo/TA/logos/${client}/${name}`;
            const url = await getDownloadURL(ref(storage, refPath));
            logoCache[name] = url;
            return url;
        } catch {
            logoCache[name] = null;
            return null;
        } finally {
            delete inFlight[name];
        }
    })();

    inFlight[name] = promise;
    return promise;
}

/**
 * Preloads all expected logos into the cache.
 */
export async function preloadLogos() {
    const names = ['logo1-big.png', 'logo1-small.png', 'logo2-big.png', 'logo2-small.png'];
    await Promise.all(names.map(getLogo));
}

// ─── React Hook ──────────────────────────────────────────────────────────────

/**
 * Hook to fetch a single logo URL by name.
 *
 * @param {string} name - e.g. 'logo1-big.png'
 * @returns {{ url: string | null, loading: boolean, error: Error | null }}
 *
 * @example
 * const { url, loading } = useLogo('logo1-big.png');
 * if (!loading && url) return <img src={url} />;
 */
export function useLogo(name) {
    const [url, setUrl] = useState(logoCache[name] !== undefined ? logoCache[name] : undefined);
    const [loading, setLoading] = useState(logoCache[name] === undefined);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!name) return;

        // Already cached — no fetch needed
        if (logoCache[name] !== undefined) {
            setUrl(logoCache[name]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        getLogo(name).then((result) => {
            if (!cancelled) {
                setUrl(result);
                setLoading(false);
            }
        }).catch((err) => {
            if (!cancelled) {
                setError(err);
                setLoading(false);
            }
        });

        return () => { cancelled = true; };
    }, [name]);

    return { url, loading, error };
}

// ─── Fork-Join Hook ───────────────────────────────────────────────────────────

/**
 * Hook to fetch multiple logos in parallel (fork-join).
 * Returns a single `loading` flag (true while ANY logo is still pending)
 * and a `urls` map of { [name]: url | null }.
 *
 * SWR-like: if all requested names are already cached, loading is false
 * immediately — no network round-trip occurs.
 *
 * @param {string[]} names - e.g. ['logo1-big.png', 'logo2-big.png']
 * @returns {{ urls: Record<string, string|null>, loading: boolean }}
 */
export function useLogos(names) {
    const namesRef = useRef(names);
    namesRef.current = names;

    // Initialise from cache immediately — avoids a loading flash when cached
    const buildState = (nameList) => {
        const urls = {};
        let anyLoading = false;
        for (const n of nameList) {
            if (logoCache[n] !== undefined) {
                urls[n] = logoCache[n];
            } else {
                urls[n] = undefined;
                anyLoading = true;
            }
        }
        return { urls, loading: anyLoading };
    };

    const [state, setState] = useState(() => buildState(names));

    useEffect(() => {
        const currentNames = namesRef.current;
        const initial = buildState(currentNames);

        // All already cached — nothing to do
        if (!initial.loading) {
            setState(initial);
            return;
        }

        let cancelled = false;
        setState({ urls: initial.urls, loading: true });

        // Fork: start all fetches in parallel, deduplicated via inFlight
        Promise.all(currentNames.map(getLogo)).then((results) => {
            if (!cancelled) {
                const urls = {};
                currentNames.forEach((n, i) => { urls[n] = results[i]; });
                // Join: set loading false only after ALL settle
                setState({ urls, loading: false });
            }
        });

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [names.join('|')]);

    return state;
}